import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, ChevronRight, Globe, ArrowRight, Navigation } from 'lucide-react';
import { getBranches } from '../services/BranchService';
import { normalizeImageUrl } from '../utils/imageUtils';
import { getBranchAddressText, getBranchMapUrl } from '../utils/branchUtils';
import { useData } from '../context/DataContext';
import AOS from 'aos';
import 'aos/dist/aos.css';

const BranchesPage = () => {
    const { branches, loading, settings } = useData();

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    return (
        <main className="branches-page bg-gray-50 min-h-screen">
            {/* ════ MINIMAL BREADCRUMBS ════ */}
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="text-slate-900 font-bold">Hệ thống chi nhánh</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <section className="py-12 lg:py-20">
                <div className="container max-w-7xl mx-auto px-4">
                    <div className="flex flex-col items-center mb-20">
                        <span className="text-red-600 font-bold uppercase tracking-[0.3em] text-xs mb-4">Địa điểm tin cậy</span>
                        <h2 className="text-3xl md:text-4xl font-black text-slate-900 text-center uppercase mb-6">
                            Tìm Chi Nhánh Gần Bạn Nhất
                        </h2>
                        <div className="w-24 h-1.5 bg-black rounded-full"></div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {branches.map((branch, index) => (
                                <div
                                    key={branch._id || branch.id || index}
                                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 flex flex-col"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 30}
                                >
                                    {/* Compact Image Container */}
                                    <div className="relative aspect-video overflow-hidden">
                                        <img
                                            src={normalizeImageUrl(branch.imageUrl)}
                                            alt={branch.name}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-sm text-white text-[8px] font-bold rounded-md uppercase tracking-wider">
                                            Trung Anh Garage
                                        </div>
                                    </div>

                                    {/* Compact Content Container */}
                                    <div className="p-4 flex flex-col">
                                        <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors line-clamp-1" title={branch.name}>
                                            {branch.name}
                                        </h3>

                                        <div className="space-y-1.5 mb-3">
                                            <div className="flex items-start gap-2">
                                                <MapPin className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                                                <p className="text-[11px] text-slate-500 leading-tight">
                                                    {getBranchAddressText(branch.address) || 'Địa chỉ đang cập nhật'}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <Phone className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                                <p className="text-[11px] font-bold text-slate-700">
                                                    {branch.phone || settings?.hotline}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-slate-50 flex items-center justify-between">
                                            <Link
                                                to={`/he-thong-chi-nhanh/${branch.slug}`}
                                                className="text-[11px] font-bold text-red-600 hover:text-red-700 transition-colors uppercase tracking-wider"
                                            >
                                                Xem chi tiết →
                                            </Link>

                                            <a
                                                href={getBranchMapUrl(branch) || `https://www.google.com/maps?q=${encodeURIComponent(branch.name)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-slate-400 hover:text-red-600 transition-colors p-1"
                                                title="Chỉ đường"
                                            >
                                                <Navigation size={14} />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && branches.length === 0 && (
                        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
                            <p className="text-gray-400 font-medium">Hiện tại chưa có chi nhánh nào được cập nhật.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-slate-900 py-16">
                <div className="container max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-black p-8 md:p-12 rounded-[2.5rem] shadow-2xl overflow-hidden relative">
                        <div className="absolute right-0 top-0 opacity-10 pointer-events-none">
                            <Globe size={300} />
                        </div>
                        <div className="relative z-10 text-center md:text-left">
                            <h3 className="text-white text-3xl font-black uppercase mb-2">Trung Anh Garage</h3>
                            <p className="text-white/80 font-medium">Chăm sóc xế yêu của bạn một cách chuyên nghiệp nhất</p>
                        </div>
                        <div className="relative z-10">
                            <a href={`tel:${settings?.hotline}`} className="inline-flex items-center gap-3 bg-white text-slate-900 font-black px-10 py-5 rounded-2xl hover:bg-slate-900 hover:text-white transition-all duration-300 shadow-xl">
                                <Phone size={24} /> {settings?.hotline}
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default BranchesPage;
